/**
 * `collection_read` and `request_read` — the READ side, which phase 1 put in
 * the *"never, without a new owner decision"* row **because requests are where
 * credentials live**. This is that decision, taken and scoped.
 *
 * Neither handler reads a field itself. Both hand the record to
 * `mcp/requestProjection.js` and return what comes back, so there is exactly
 * ONE place in the codebase that decides what may leave a stored request — and
 * the sentinel sweep in `mcpRequestProjection.test.js` covers that one place
 * over an OPEN record shape.
 *
 * ## Why `collection_read` is a summary and `request_read` is the detail
 *
 * A collection can hold hundreds of requests. Projecting every one of them in
 * full would be a large answer, most of it unread, over the most sensitive
 * record in the app — so `collection_read` returns per-request
 * `{id, name, method, url, folderId, description, authType, headerNames}` and
 * `request_read` returns the full projection for the ONE request the model has
 * decided it cares about. That is not a size optimisation dressed up as a
 * safety property: a narrower default answer is genuinely less to get wrong,
 * and the header NAMES in the summary are enough for a model to know which
 * request to read.
 */
import { getFolders, normalizeParentId } from '../../utils/collectionFolders';
import {
  currentWorkspaceOrRefuse,
  findCollection,
  findRequest,
  readCollections,
  workspaceReport,
} from '../collectionsDb';
import { collectionNotFound } from '../collectionRecords';
import { revOfCollection, revOfRequest } from '../collectionRev';
import { mcpError } from '../errors';
import { normalizeFolderVariables } from '../../utils/folderVariables';
import { authInheritanceOf } from '../authInheritance';
import { projectRequestWithProtocol, requestKind } from '../protocolProjection';
import { projectAuth, projectKvRows, projectUrl } from '../requestProjection';

/** The folder tree, flattened, in sibling order. Names and parentage only. */
export function foldersOf(collection) {
  return getFolders(collection)
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((f) => ({
      id: String(f.id),
      name: String(f.name || ''),
      parentFolderId: normalizeParentId(f.parent_id),
      description: typeof f.description === 'string' ? f.description : '',
    }));
}

const hasText = (value) => typeof value === 'string' && value.trim() !== '';

/**
 * `collection_read`'s folders: `foldersOf` plus the three things the folder
 * page can set that change how the requests inside are sent (card 9334908f).
 * `collections_list` keeps the lean rows — it lists every collection.
 *
 *   * `auth` — through `projectAuth`, `inherit` when the folder sets none
 *     (absent IS how the app stores "Inherit from parent");
 *   * `variableNames` — the folder's variable NAMES. Never a value, the same
 *     rule `environments_list` keeps for every other scope (O7);
 *   * `hasScripts` — whether a pre-request or test script exists. Scripts are
 *     neither readable nor writable over MCP (O33), so this is a boolean.
 */
export function folderDetailsOf(collection) {
  const byId = new Map(getFolders(collection).map((f) => [String(f.id), f]));
  return foldersOf(collection).map((row) => {
    const f = byId.get(row.id) || {};
    return {
      ...row,
      auth: projectAuth(f.auth && typeof f.auth === 'object' ? f.auth : { type: 'inherit' }),
      variableNames: normalizeFolderVariables(f.variables).map((v) => v.key),
      hasScripts: hasText(f.preRequestScript) || hasText(f.testScript),
    };
  });
}

/**
 * One row of the summary. The url goes through the SAME `projectUrl` the full
 * projection uses — there is no second, laxer path for a "short" answer, which
 * is exactly how a leak gets into a listing after the detail view was made
 * safe.
 */
export function requestSummary(request) {
  const url = projectUrl(request?.url, request?.params);
  const headers = projectKvRows(request?.headers, 'header');
  const auth = projectAuth(request?.auth);
  return {
    id: String(request?.id ?? ''),
    // The rev `request_update`, `request_move` and the two mock tools accept.
    // It is a content hash over this record (`mcp/collectionRev.js`), computed
    // by the SAME function the writer checks against — a second computation
    // here would be a second mechanism, and the round trip is asserted rather
    // than the string.
    rev: revOfRequest(request),
    name: String(request?.name || ''),
    method: String(request?.method || 'GET').toUpperCase(),
    url: url.url,
    folderId: request?.folder_id == null ? null : String(request.folder_id),
    description: typeof request?.description === 'string' ? request.description : '',
    authType: auth.type,
    // NAMES, always. Values need the allowlist and the pointer rule, and a
    // summary is not the place to run them.
    headerNames: headers.map((h) => h.key),
    hasBody: String(request?.bodyType || 'none') !== 'none',
    // http | grpc | websocket | sse — a gRPC row's method/url are not what it sends.
    type: requestKind(request),
  };
}

export async function collectionRead(args = {}) {
  const gate = currentWorkspaceOrRefuse();
  if (gate.error) return gate.error;
  const { workspace } = gate;

  const collection = findCollection(readCollections(), args.collection_id);
  if (!collection) return collectionNotFound(args.collection_id);

  const requests = (collection.requests || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map(requestSummary);

  return {
    workspace: workspaceReport(workspace),
    collection: {
      id: String(collection.id),
      // The rev `collection_update` and `folder_rename` accept. It covers the
      // collection WITHOUT its requests, mirroring how the row is stored — so
      // adding a request does not invalidate a rev someone is holding in order
      // to rename a folder.
      rev: revOfCollection(collection),
      name: String(collection.name || ''),
      description: typeof collection.description === 'string' ? collection.description : '',
      // The collection-level auth block, through the same projection as a
      // request's: `inherit` on a request means THIS, so a diagnosis that
      // could not see it would be blind to the most common auth mistake.
      auth: projectAuth(collection.settings?.collection_auth),
      folders: folderDetailsOf(collection),
      requestCount: requests.length,
      requests,
    },
    scopesConsulted: [],
    scopesUnavailable: [],
  };
}

export async function requestRead(args = {}) {
  const gate = currentWorkspaceOrRefuse();
  if (gate.error) return gate.error;
  const { workspace } = gate;

  const found = findRequest(readCollections(), args.request_id);
  if (!found) {
    return mcpError(
      'not_found',
      'No request with that id in the open workspace. Call collections_list with include_requests and use an id it returns.',
      { requestId: String(args.request_id ?? '') }
    );
  }

  const inheritance = authInheritanceOf(found.request, found.collection);
  return {
    workspace: workspaceReport(workspace),
    rev: revOfRequest(found.request),
    collectionRev: revOfCollection(found.collection),
    // The projection plus its `protocol` block: a gRPC or WebSocket request
    // reports the fields it really sends, not only the HTTP ones it ignores.
    request: projectRequestWithProtocol(found.request, {
      collectionId: found.collection.id,
      collectionName: found.collection.name,
    }),
    // The parent's auth, because `auth.type: "inherit"` is meaningless without
    // it and it is one of the three things that actually break a request.
    collectionAuth: projectAuth(found.collection.settings?.collection_auth),
    // ...and the folders in between, nearest first, because a folder can
    // override the collection (card 9334908f). `effectiveAuthFrom` is computed
    // by the app's own resolver, so it says what Send will do.
    folderAuthChain: inheritance.folderAuthChain,
    effectiveAuthFrom: inheritance.effectiveAuthFrom,
    effectiveAuthType: inheritance.effectiveAuthType,
    effectiveAuthFolderId: inheritance.effectiveAuthFolderId,
    scopesConsulted: [],
    scopesUnavailable: [],
  };
}
